// src/components/crm/PaymentSettings.js
import React, { useState, useEffect } from 'react';
import { functions, db } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore'; 
import { CreditCard, CheckCircle, Loader2, AlertCircle, RefreshCw, XCircle } from 'lucide-react';
import { restrictedBannerKind } from '../../utils/orgPayments';

// payhide-7e4c1b05 — the restricted card's wording, by banner kind (src/utils/orgPayments.js).
// Headlines match the Seam 4 operator email (functions/utils/restrictedNotice.js).
const RESTRICTED_COPY = {
    unfinished: {
        headline: 'Finish setting up Stripe',
        line: 'Finish setting up Stripe to accept online payments.',
        showFinish: true,
    },
    reviewing: {
        headline: 'Stripe is reviewing your details',
        line: 'Stripe is reviewing your details. Online payments will turn on once they’re done.',
        showFinish: false,
    },
    paused: {
        headline: 'Online payments are paused',
        line: 'Stripe has paused your online payments and needs more information.',
        showFinish: true,
    },
};

const PaymentSettings = ({ currentUserProfile }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false); 
    const [verificationError, setVerificationError] = useState(null); 
    const [error, setError] = useState('');

    // --- LOCAL STATE ---
    const [isStripeConnected, setIsStripeConnected] = useState(false);
    const [stripeAccountId, setStripeAccountId] = useState(null);
    // payhide-7e4c1b05 — null, or 'unfinished' | 'reviewing' | 'paused' when the org has a
    // Stripe account that can't take charges. Derived from the stored stripeChargeStatus.
    const [bannerKind, setBannerKind] = useState(null);

    // --- LISTEN TO ORGANIZATION STATUS ---
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;

        const orgRef = doc(db, 'organizations', orgId);
        const unsubscribe = onSnapshot(orgRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setIsStripeConnected(data.stripeOnboardingComplete === true);
                setStripeAccountId(data.stripeAccountId);
                setBannerKind(restrictedBannerKind(data));
            }
        });
        return () => unsubscribe();
    }, [currentUserProfile?.organizationId]);

    // payhide-7e4c1b05 — ask Stripe for the current status once per visit. The callable
    // writes the org doc only if Stripe's answer changed, and the listener above re-renders.
    // A failure is not shown: the stored status keeps driving the page.
    useEffect(() => {
        const refresh = httpsCallable(functions, 'refreshStripeChargeStatus');
        refresh().catch((err) => console.warn('refreshStripeChargeStatus failed', err));
    }, []);

    // --- VERIFICATION LOGIC ---
    const verifyStripeConnection = async () => {
        setIsVerifying(true);
        setVerificationError(null);
        setError('');
        
        try {
            const finalizeStripeConnection = httpsCallable(functions, 'finalizeStripeConnection');
            const result = await finalizeStripeConnection();

            if (result.data && result.data.success) {
                window.history.replaceState({}, document.title, window.location.pathname);
            } else {
                setVerificationError("Stripe reported the setup is incomplete. Please try connecting again.");
            }
        } catch (err) {
            setVerificationError(`Verification failed: ${err.message}`);
        } finally {
            setIsVerifying(false);
        }
    };

    // --- HANDLE RETURN FROM STRIPE ---
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if ((urlParams.get('success') || urlParams.get('reauth')) && !isStripeConnected && !isVerifying) {
            verifyStripeConnection();
        }
    }, [isStripeConnected, isVerifying]); 

    const handleConnectStripe = async () => {
        setIsLoading(true);
        setError('');
        try {
            const createStripeConnectedAccount = httpsCallable(functions, 'createStripeConnectedAccount');
            const result = await createStripeConnectedAccount({ origin: window.location.origin });
            window.location.href = result.data.url;
        } catch (err) {
            setError(err.message || 'Could not connect to Stripe. Please try again.');
            setIsLoading(false);
        }
    };

    const handleDisconnectStripe = async () => {
        if (!window.confirm("Are you sure? This will disconnect your Stripe account.")) return;

        setIsDisconnecting(true);
        setError('');

        try {
            const disconnectStripe = httpsCallable(functions, 'disconnectStripe');
            await disconnectStripe();
        } catch (err) {
            setError(`Failed to disconnect: ${err.message}`);
        } finally {
            setIsDisconnecting(false);
        }
    };

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                <CreditCard className="text-blue-600" /> Payment Gateway
            </h2>
            
            <div className="border-t border-gray-100 pt-4">
                
                {/* --- VERIFICATION UI --- */}
                {isVerifying && (
                    <div className="mb-4 p-4 bg-blue-50 text-blue-700 rounded-lg flex items-center gap-3 animate-pulse">
                        <Loader2 className="animate-spin" size={20} />
                        <span className="font-medium">Finalizing Stripe connection...</span>
                    </div>
                )}

                {/* --- ERROR UI --- */}
                {verificationError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                        <div className="flex items-center gap-2 font-bold mb-1">
                            <AlertCircle size={20} /> Connection Issue
                        </div>
                        <p className="text-sm mb-3">{verificationError}</p>
                        <button onClick={verifyStripeConnection} className="text-xs flex items-center gap-1 bg-white border border-red-300 px-3 py-1.5 rounded hover:bg-red-50 font-semibold shadow-sm">
                            <RefreshCw size={12} /> Retry
                        </button>
                    </div>
                )}

                {bannerKind && RESTRICTED_COPY[bannerKind] ? (
                    // --- RESTRICTED STATE (payhide-7e4c1b05) ---
                    // The org has a Stripe account that can't take charges. Replaces BOTH the
                    // green "ready" card (QI: onboarding flag true, Stripe paused) and the grey
                    // "Connect" card (Test Co.: never finished). Finish setup reuses
                    // createStripeConnectedAccount, which keeps the same account id and returns
                    // a fresh Stripe onboarding link.
                    <div className="border border-amber-200 rounded-xl bg-amber-50/60 p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-full bg-white border border-amber-200 text-amber-600 shadow-sm shrink-0">
                                    <AlertCircle size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">{RESTRICTED_COPY[bannerKind].headline}</h3>
                                    <p className="text-sm text-gray-700 mt-1">{RESTRICTED_COPY[bannerKind].line}</p>
                                    <p className="text-sm text-gray-600 mt-1">Until then, customers can't pay invoices or deposits online.</p>
                                    {stripeAccountId && (
                                        <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded font-mono border border-gray-200">
                                            ID: {stripeAccountId}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col sm:items-end gap-3 mt-2 sm:mt-0 pl-[60px] sm:pl-0">
                                {RESTRICTED_COPY[bannerKind].showFinish && (
                                    <button
                                        onClick={handleConnectStripe}
                                        disabled={isLoading || isVerifying}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm transition-all w-full sm:w-auto"
                                    >
                                        {isLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                                        {isLoading ? 'Opening Stripe...' : 'Finish setup'}
                                    </button>
                                )}
                                <button
                                    onClick={handleDisconnectStripe}
                                    disabled={isDisconnecting}
                                    className="flex items-center justify-center gap-2 px-4 py-2 text-red-600 bg-white border border-red-200 text-sm font-semibold hover:bg-red-50 hover:border-red-300 rounded-lg transition-all shadow-sm w-full sm:w-auto"
                                >
                                    {isDisconnecting ? <Loader2 className="animate-spin" size={16}/> : <XCircle size={16} />}
                                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect Account'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : isStripeConnected ? (
                    // --- CONNECTED STATE (RESPONSIVE) ---
                    <div className="border border-green-200 rounded-xl bg-green-50/50 p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            
                            {/* Icon + Text */}
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-full bg-white border border-indigo-100 text-indigo-600 shadow-sm shrink-0">
                                    <CreditCard size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Stripe Payments</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        You are ready to accept payments.
                                    </p>
                                    {stripeAccountId && (
                                        <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded font-mono border border-gray-200">
                                            ID: {stripeAccountId}
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Status + Button (Stacked on mobile, Row on desktop) */}
                            <div className="flex flex-col sm:items-end gap-3 mt-2 sm:mt-0 pl-[60px] sm:pl-0">
                                <div className="flex items-center gap-2 text-green-700 bg-green-100/50 px-3 py-1 rounded-full w-fit">
                                    <CheckCircle size={16} className="fill-green-600 text-white" /> 
                                    <span className="text-sm font-bold">Connected</span>
                                </div>
                                
                                <button 
                                    onClick={handleDisconnectStripe}
                                    disabled={isDisconnecting}
                                    className="flex items-center justify-center gap-2 px-4 py-2 text-red-600 bg-white border border-red-200 text-sm font-semibold hover:bg-red-50 hover:border-red-300 rounded-lg transition-all shadow-sm w-full sm:w-auto"
                                >
                                    {isDisconnecting ? <Loader2 className="animate-spin" size={16}/> : <XCircle size={16} />}
                                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect Account'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    // --- DISCONNECTED STATE ---
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-center sm:text-left">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Accept Credit Cards Online</h3>
                        <p className="mb-6 text-gray-600 leading-relaxed max-w-2xl">
                            Connect a Stripe account to let your clients pay invoices instantly via credit card. 
                            Funds are deposited directly into your bank account securely.
                        </p>
                        <button
                            onClick={handleConnectStripe}
                            disabled={isLoading || isVerifying}
                            className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex justify-center items-center gap-2 shadow-md hover:shadow-lg transition-all active:scale-95"
                        >
                            {isLoading ? <Loader2 className="animate-spin" size={20} /> : <CreditCard size={20} />}
                            {isLoading ? 'Connecting...' : 'Connect Stripe Account'}
                        </button>
                    </div>
                )}
                
                {error && (
                    <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
                        <AlertCircle size={16} /> {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSettings;